/**
 * CANONICAL MODULE — app-platform/shared/graphClient.ts
 *
 * Copied byte-for-byte into every app (`src/lib/graphClient.ts`). It imports only its canonical
 * siblings: `authToken.ts` (for `InteractiveSignInRequiredError`), `graphErrors.ts`,
 * `graphRetry.ts` and `writePrecondition.ts`. **No MSAL import** — the token getter is injected.
 * Built for work order W10 (task 2.b) from
 * [design/W10-sync-transport.md](../design/W10-sync-transport.md) §3.3.
 *
 * **Fix bugs HERE, then copy outward. Never edit an app's copy in place.**
 *
 * The HTTP transport under every app's sync, and nothing above it: no unit (recipe, doc, chapter,
 * day) is known here, and no conflict is ever *resolved* here. That is the caller's policy (§2.1).
 *
 * ── What every request gets, through one internal loop ───────────────────────────────────────────
 *
 * 1. **A token per attempt.** The first 401 forces a refresh and is NOT counted against the retry
 *    budget; a second 401 throws `InteractiveSignInRequiredError` (authToken.ts note 3). Before
 *    W10 only bindr did this; the other four retried a dead token, or gave up as a generic error.
 * 2. **The canonical retry policy** (graphRetry.ts): 408/429/5xx-transient and network throws, full
 *    jitter, capped, `Retry-After` as a floor. A 429/503 pauses every request through this client.
 * 3. **Typed failures.** A fetch that keeps rejecting → `GraphNetworkError`; a non-OK status →
 *    `GraphError` (or `GraphThrottledError`) with Graph's code and body read; a refused precondition
 *    → `DocConflictError`. Never a raw `TypeError`, never a bare `Error`.
 * 4. **The server's `Date` header** to `onServerDate` (quil's clock), when the app asks for it.
 * 5. **A concurrency gate** (default 4, contently's) that is held only while a request is on the
 *    wire — never across a backoff sleep, which is how contently's version stalled the whole client.
 *
 * ── Addressing ───────────────────────────────────────────────────────────────────────────────────
 * Paths are app-relative (`recipes/abc.json`), resolved against the configured root.
 * `{ kind: 'approot' }` is the destination for all five apps at the end of W10 (Q1's
 * `Files.ReadWrite.AppFolder` scope denies `/me/drive/root:` addressing outright). `{ kind: 'path' }`
 * exists only so a pilot can land the transport before the scope flip; no app ships on it.
 *
 * `verifyRoot()` runs once per client before the first call under approot, and **fails closed** with
 * `AppRootMismatchError` unless Graph's approot is `/Apps/<expectFolderName>`. Since 2026-10-07 that
 * includes a raw `request()` (the bindr scope flip found it did not, and wrapped four raw calls in a
 * local `rooted()`); pass `{ outsideRoot: true }` for a call that targets outside the root (`/me`).
 *
 * ── Added after the contently pilot (2026-10-07, design §8 items 11-15) ───────────────────────────
 * `ensureFolder('')` creates a missing `{ kind: 'path' }` root; `forgetFolder(rel)` drops one cached
 * folder id (and its descendants') without `reset()`'s root re-check; `uploadLarge` takes
 * `{ onProgress, signal }` and checks the committed size; `listAll(..., { missing: 'null' })` tells
 * a missing folder from an empty one. Tests use `graphTestKit.ts`.
 *
 * ── Added after the first real-Graph live run (2026-10-07) ────────────────────────────────────────
 * Real Graph accepts an `If-Match` write onto a file that no longer exists and CREATES it (201),
 * rather than refusing with 404. The transport cannot prevent that, so it detects it: an `If-Match`
 * write (simple or upload session) answered 201 returns `WrittenItem.recreated: true`. Not thrown —
 * the bytes are written; the caller's orchestration decides (design §3.1).
 *
 * ── Added for load speed (2026-10-14, contently's BRIEF-hub-load-speed.md) ─────────────────────────
 * **Early reads (opt-in, `rememberRoot`).** The approot check costs one ~2 s round trip on every cold
 * load. An app that passes `rememberRoot` has a passed check remembered per account; on a later
 * session for the same account, reads go out alongside the re-check and are held until it passes.
 * Writes always wait. A failed check rejects the held reads with its own error and forgets the
 * memory; a pass that names a different folder id discards them and reads again. Without the option,
 * nothing changes. Apps clear the memory on sign-out and erase (`forgetRememberedRoot`).
 * **Early reads on a new device (opt-in, `rememberRoot.earlyWithoutMemory`, 2026-10-24).** The same
 * hold-until-passed rule for an account with nothing remembered yet, so a new device's first listing
 * is not queued behind the check. Off by default; see the option for what it trades.
 * **Background priority.** `priority: 'background'` (raw requests, `listAll`, `readWithTag`,
 * `uploadLarge`) queues behind every foreground request for a transport slot. Default foreground.
 */

import { InteractiveSignInRequiredError } from './authToken'
import {
  AppRootMismatchError,
  DocConflictError,
  GraphError,
  GraphNetworkError,
  GraphThrottledError,
  graphErrorCode,
} from './graphErrors'
import {
  DEFAULT_RETRY,
  createThrottleGate,
  isRetryable,
  isThrottle,
  retryDelayMs,
  type RetryPolicy,
} from './graphRetry'
import { contentTagOf, type WritePrecondition } from './writePrecondition'

export const GRAPH_BASE = 'https://graph.microsoft.com/v1.0'

/** Graph's documented ceiling for a simple (single-request) content upload. */
export const SIMPLE_UPLOAD_MAX = 4 * 1024 * 1024
/**
 * Upload-session chunk: a multiple of 320 KiB, as Graph requires; bindr's 3.2 MiB.
 * Exported for tests only (app-platform 8.3): no app code calls it.
 */
export const UPLOAD_CHUNK = 10 * 320 * 1024
/** Resumes (re-reads of `nextExpectedRanges`) one large upload may spend before it gives up. */
const MAX_UPLOAD_RESUMES = 3

export type GraphRoot = { kind: 'approot'; expectFolderName: string } | { kind: 'path'; path: string }

export interface GraphClientConfig {
  /** The canonical `TokenGetter` from authToken.ts. */
  getToken: (o?: { forceRefresh?: boolean }) => Promise<string>
  root: GraphRoot
  retry?: Partial<RetryPolicy>
  /** Requests on the wire at once. Default 4. */
  maxConcurrent?: number
  /** Receives the `Date` header of every response (quil's clock). Others omit it. */
  onServerDate?: (header: string) => void
  /**
   * Opt in to early reads (2026-10-14, the hub load-speed brief, Change A). Absent = today's
   * behaviour: nothing runs until `verifyRoot()` has passed. Present: a passed check is remembered
   * per account (`rememberedRootKey`), and on a later session for the SAME account a READ (GET/HEAD,
   * `listAll`, `readWithTag`, `delta`) goes out at once, alongside the re-check, instead of after it.
   * Its result is only delivered once the re-check passes. Writes always wait. Approot only.
   */
  rememberRoot?: {
    /** The signed-in account's `homeAccountId`, read at call time. `null` = no early reads. */
    accountId: () => string | null | undefined
    /** Default `globalThis.localStorage`. Every access is guarded: a throwing store = no memory. */
    storage?: RootMemoryStorage
    /**
     * 2026-10-24 (contently Track G, new-device cold start). Absent/false = today: an account with
     * no remembered approot (a new device, a cleared store) waits for the check before any read.
     * True: such a READ also goes out beside the check and is delivered only once the check passes;
     * a failed check rejects it with the check's own error, exactly as a remembered early read.
     * What it gives up: with no remembered id there is nothing to confirm the read reached the folder
     * the check saw, so it trusts `special/approot` as every request after a passed check already
     * does. And on an account whose approot does not exist yet, the read is a second first access
     * beside the check (Graph creates the folder on first use); if that ever made a duplicate
     * folder, the check names it and fails closed. Still needs an account (`accountId`).
     */
    earlyWithoutMemory?: boolean
  }
  fetch?: typeof fetch
  sleep?: (ms: number) => Promise<void>
  now?: () => number
  random?: () => number
}

/** The two tags Graph gives a written item: the content tag for the next `If-Match`, and the eTag. */
export interface WrittenItem {
  /** `cTag ?? eTag` — what a later content precondition names. */
  tag: string | null
  /** The item eTag — what a later DELETE's `If-Match` names. */
  eTag: string | null
  id: string | null
  /**
   * `true` only when a write sent with an `If-Match` precondition was answered **201 Created**: the
   * file the tag named had vanished (deleted on another device, or moved) and Graph silently
   * recreated it with these bytes. Real Graph does NOT refuse an If-Match onto a missing file
   * (observed by the live harness, contently, 2026-10-07): it creates it. The bytes are already
   * written, so this is reported, never thrown — the caller's orchestration decides whether a
   * delete on another device should win (W10 §3.1). Absent on every other write: a 200 replace, or
   * a 201 from `expect-absent` / `none`, which is an ordinary create.
   */
  recreated?: true
}

export interface TaggedRead {
  /** `cTag ?? eTag`, read BEFORE the bytes. */
  tag: string
  eTag: string | null
  /** The `/content` response; read it with `.text()`, `.json()` or `.arrayBuffer()`. */
  response: Response
}

export interface DeltaResult<T> {
  items: T[]
  /** Store this and pass it to the next `delta()` call. */
  deltaLink: string
  /** True when Graph answered 410 and the delta was restarted from scratch: re-reconcile everything. */
  reset: boolean
}

export interface RequestOptions {
  /** Statuses returned to the caller instead of thrown (e.g. `[404]` for "may not exist"). */
  accept?: readonly number[]
  /** Marks a conditional write, so a refusal maps to `DocConflictError` rather than `GraphError`. */
  conditional?: { path: string; pre: WritePrecondition }
  /** Send no `Authorization` header (upload-session URLs are pre-authenticated and reject one). */
  anonymous?: boolean
  /**
   * Skip the fail-closed root check for a raw `request()` that legitimately targets something outside
   * the app's root (`/me`, `/me/drive` quota). Every other raw request is checked first, exactly like
   * the path methods. Ignored by the path methods themselves.
   */
  outsideRoot?: boolean
  /** `'background'` waits for a free slot behind every queued foreground request (Change B). */
  priority?: RequestPriority
}

/**
 * Foreground (the default) is everything the user is waiting on. A `'background'` request (a cover,
 * an image, a font, a backups listing) only takes a free transport slot when no foreground request
 * is queued, so it can never hold up a data read. It still runs whenever no foreground work waits.
 */
export type RequestPriority = 'foreground' | 'background'

export type RootMemoryStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

/** The remembered-approot key: account-scoped in the W12 `${prefix}@${accountId}` form, with the
 *  prefix `graph-approot:<folder>`, so an app's erase plan can list it like its other scoped keys.
 * Exported for tests only (app-platform 8.3): no app code calls it.
 */
export function rememberedRootKey(expectFolderName: string, accountId: string): string {
  return `graph-approot:${expectFolderName.toLowerCase()}@${accountId}`
}

function defaultStorage(): RootMemoryStorage | null {
  try {
    return typeof globalThis.localStorage === 'undefined' ? null : globalThis.localStorage
  } catch {
    return null
  }
}

/** Drop the remembered approot for one account (sign-out, erase this device). Never throws. */
export function forgetRememberedRoot(
  expectFolderName: string,
  accountId: string,
  storage: RootMemoryStorage | null = defaultStorage(),
): void {
  try {
    storage?.removeItem(rememberedRootKey(expectFolderName, accountId))
  } catch {
    /* a store that cannot be written holds nothing to forget */
  }
}

interface RememberedRoot {
  v: 1
  account: string
  id: string | null
  /** `<parentReference.path>/<name>`, e.g. `/drive/root:/Apps/Contently`. */
  path: string
}

export interface ListOptions {
  /**
   * What a folder that does not exist (404 on the first page) comes back as. `'empty'` (the default,
   * and the pre-2026-10-07 behaviour) is `[]`, indistinguishable from an empty folder; `'null'` is
   * `null`, for a caller that must tell "never created" from "emptied".
   */
  missing?: 'empty' | 'null'
  priority?: RequestPriority
}

export interface ReadOptions {
  priority?: RequestPriority
}

export interface UploadOptions {
  /** Called after every chunk Graph accepts, with the bytes it has confirmed and the total. */
  onProgress?: (sent: number, total: number) => void
  /** Aborts between chunks and on the wire. The abort error is thrown unchanged, never retried. */
  signal?: AbortSignal
  priority?: RequestPriority
}

export interface GraphClient {
  request(url: string, init?: RequestInit, opts?: RequestOptions): Promise<Response>
  itemUrl(rel: string): string
  contentUrl(rel: string): string
  childrenUrl(rel: string): string
  verifyRoot(): Promise<void>
  listAll<T = Record<string, unknown>>(rel: string, select?: string, opts?: ListOptions & { missing?: 'empty' }): Promise<T[]>
  listAll<T = Record<string, unknown>>(
    rel: string,
    select: string | undefined,
    opts: ListOptions & { missing: 'null' },
  ): Promise<T[] | null>
  readWithTag(rel: string, opts?: ReadOptions): Promise<TaggedRead | null>
  write(rel: string, body: BodyInit, contentType: string, pre: WritePrecondition): Promise<WrittenItem>
  uploadLarge(
    rel: string,
    bytes: Blob | ArrayBuffer | Uint8Array,
    pre: WritePrecondition,
    opts?: UploadOptions,
  ): Promise<WrittenItem>
  remove(rel: string, pre?: { kind: 'if-match'; tag: string }): Promise<void>
  /** The folder's id, creating it and any missing ancestors. `''` is the root: under
   *  `{ kind: 'path' }` a missing root path is created segment by segment; under approot a missing
   *  root is an error (Graph creates the approot itself on first use). */
  ensureFolder(rel: string): Promise<string>
  /** Drop one folder's cached id, and its descendants', after the folder was deleted or moved. The
   *  verified root stays verified. `forgetFolder('')` drops every cached id. */
  forgetFolder(rel: string): void
  delta<T = Record<string, unknown>>(deltaLink?: string): Promise<DeltaResult<T>>
  /** Forget the verified root and the folder-id cache (sign-out, account switch). Keeps the
   *  remembered approot: it is per account, so another account never matches it. */
  reset(): void
  /** Drop the remembered approot for the current account (sign-out, erase). No-op without
   *  `rememberRoot` or an account. */
  forgetRememberedRoot(): void
}

/** One path segment at a time, so `/` stays a separator and nothing else leaks into the URL. */
function encodeRel(rel: string): string {
  return rel
    .split('/')
    .filter((s) => s !== '')
    .map((s) => encodeURIComponent(s))
    .join('/')
}

function parentRel(rel: string): string {
  const clean = rel.replace(/^\/+|\/+$/g, '')
  const i = clean.lastIndexOf('/')
  return i < 0 ? '' : clean.slice(0, i)
}

function leafName(rel: string): string {
  const clean = rel.replace(/^\/+|\/+$/g, '')
  return clean.slice(clean.lastIndexOf('/') + 1)
}

function byteLength(body: BodyInit): number | null {
  if (typeof body === 'string') return new TextEncoder().encode(body).length
  if (body instanceof Blob) return body.size
  if (body instanceof ArrayBuffer) return body.byteLength
  if (ArrayBuffer.isView(body)) return body.byteLength
  return null
}

function toBlob(bytes: Blob | ArrayBuffer | Uint8Array): Blob {
  if (bytes instanceof Blob) return bytes
  // A copy into a plain ArrayBuffer: a Uint8Array over a SharedArrayBuffer is not a valid BlobPart.
  const view = bytes instanceof ArrayBuffer ? new Uint8Array(bytes) : bytes
  const copy = new Uint8Array(view.byteLength)
  copy.set(view)
  return new Blob([copy.buffer])
}

/** An abort is the caller's decision, never a fault to retry. Checked by name: a DOMException is
 *  not an `Error` in every runtime. */
function isAbort(error: unknown): boolean {
  return typeof error === 'object' && error !== null && (error as { name?: unknown }).name === 'AbortError'
}

/** Throw the signal's own reason when it has fired (a DOMException `AbortError` if it gave none). */
function throwIfAborted(signal: AbortSignal | null | undefined): void {
  if (!signal?.aborted) return
  const reason: unknown = signal.reason
  throw reason !== undefined ? reason : new DOMException('The operation was aborted.', 'AbortError')
}

async function jsonOf(response: Response): Promise<Record<string, unknown> | null> {
  try {
    const parsed: unknown = await response.json()
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null
  } catch {
    return null
  }
}

function writtenItemOf(item: Record<string, unknown> | null, recreated = false): WrittenItem {
  return {
    tag: contentTagOf(item),
    eTag: item && typeof item.eTag === 'string' ? item.eTag : null,
    id: item && typeof item.id === 'string' ? item.id : null,
    ...(recreated ? { recreated: true as const } : {}),
  }
}

/** An `If-Match` write answered 201 Created: the tagged file was gone, and Graph made a new one. */
function wasRecreated(pre: WritePrecondition, status: number): boolean {
  return pre.kind === 'if-match' && status === 201
}

/** The first range start in `nextExpectedRanges` (`["26-"]`, `["0-99","200-"]`), or `null`. */
function nextRangeStart(body: Record<string, unknown> | null): number | null {
  const ranges = body?.nextExpectedRanges
  if (!Array.isArray(ranges) || ranges.length === 0) return null
  const first: unknown = ranges[0]
  if (typeof first !== 'string') return null
  const start = Number(first.split('-')[0])
  return Number.isInteger(start) && start >= 0 ? start : null
}

/** A counting semaphore with two queues. Held only while a request is on the wire. A freed slot goes
 *  to the oldest queued foreground request, and to a background one only when none is queued. */
function createLimiter(max: number): { acquire(priority?: RequestPriority): Promise<() => void> } {
  let active = 0
  const foreground: Array<() => void> = []
  const background: Array<() => void> = []
  const release = (): void => {
    const next = foreground.shift() ?? background.shift()
    if (next) next()
    else active -= 1
  }
  return {
    acquire(priority: RequestPriority = 'foreground'): Promise<() => void> {
      // A free slot means both queues are empty: nothing to jump, whatever the priority.
      if (active < max) {
        active += 1
        return Promise.resolve(release)
      }
      const queue = priority === 'background' ? background : foreground
      return new Promise((resolve) => queue.push(() => resolve(release)))
    },
  }
}

const READ_METHODS = new Set(['GET', 'HEAD'])
const isReadInit = (init: RequestInit | undefined): boolean =>
  READ_METHODS.has((init?.method ?? 'GET').toUpperCase())

export function createGraphClient(cfg: GraphClientConfig): GraphClient {
  const policy: RetryPolicy = { ...DEFAULT_RETRY, ...cfg.retry }
  const doFetch: typeof fetch = cfg.fetch ?? ((input, init) => globalThis.fetch(input, init))
  const sleep = cfg.sleep ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)))
  const now = cfg.now ?? (() => Date.now())
  const random = cfg.random ?? Math.random
  const gate = createThrottleGate(now, policy.capMs)
  const limiter = createLimiter(Math.max(1, cfg.maxConcurrent ?? 4))
  const root = cfg.root

  /** The check in flight or passed. Resolves `true` when it confirmed the remembered approot, `false`
   *  when there was nothing remembered or the remembered one differs (early reads are then re-run). */
  let verified: Promise<boolean> | null = null
  let verifiedOk = false
  const folderIds = new Map<string, Promise<string>>()

  // ------------------------------------------------------------------------------- root memory
  const memory = root.kind === 'approot' ? cfg.rememberRoot : undefined
  const memoryStore = (): RootMemoryStorage | null => (memory ? (memory.storage ?? defaultStorage()) : null)
  const currentAccount = (): string | null => {
    try {
      return memory?.accountId() || null
    } catch {
      return null
    }
  }
  const folderName = root.kind === 'approot' ? root.expectFolderName : ''
  const expectedTail = `/apps/${folderName.toLowerCase()}`

  /** The remembered approot for this account, or `null` (none, corrupt, another account, unsafe). */
  function recall(account: string | null): RememberedRoot | null {
    if (!account) return null
    try {
      const raw = memoryStore()?.getItem(rememberedRootKey(folderName, account))
      if (!raw) return null
      const v = JSON.parse(raw) as Partial<RememberedRoot> | null
      if (!v || v.v !== 1 || v.account !== account || typeof v.path !== 'string') return null
      if (!v.path.toLowerCase().endsWith(expectedTail)) return null
      return { v: 1, account, id: typeof v.id === 'string' ? v.id : null, path: v.path }
    } catch {
      return null
    }
  }

  function remember(value: RememberedRoot): void {
    try {
      memoryStore()?.setItem(rememberedRootKey(folderName, value.account), JSON.stringify(value))
    } catch {
      /* no memory = today's behaviour next session */
    }
  }

  function forget(account: string | null): void {
    if (account) forgetRememberedRoot(folderName, account, memoryStore())
  }

  // ------------------------------------------------------------------------------- addressing
  /** The item expression for the root itself, without the base URL. */
  const rootExpr =
    root.kind === 'approot' ? 'me/drive/special/approot' : `me/drive/root:/${encodeRel(root.path)}`

  function itemUrl(rel: string): string {
    const r = encodeRel(rel)
    if (root.kind === 'approot') return r ? `${GRAPH_BASE}/${rootExpr}:/${r}` : `${GRAPH_BASE}/${rootExpr}`
    return r ? `${GRAPH_BASE}/${rootExpr}/${r}` : `${GRAPH_BASE}/${rootExpr}`
  }

  /** `<item>:/sub` for a path-addressed item; `special/approot/sub` for the approot itself. */
  function subUrl(rel: string, sub: string): string {
    const r = encodeRel(rel)
    if (root.kind === 'approot' && !r) return `${GRAPH_BASE}/${rootExpr}/${sub}`
    return `${itemUrl(rel)}:/${sub}`
  }

  const contentUrl = (rel: string): string => subUrl(rel, 'content')
  const childrenUrl = (rel: string): string => subUrl(rel, 'children')

  // ------------------------------------------------------------------------------- the one loop
  async function send(url: string, init: RequestInit = {}, opts: RequestOptions = {}): Promise<Response> {
    const fullUrl = /^https?:/i.test(url) ? url : `${GRAPH_BASE}/${url.replace(/^\/+/, '')}`
    let refreshed = false
    let attempt = 0

    for (;;) {
      // An abort that fired during a backoff sleep stops here, before another attempt goes out.
      throwIfAborted(init.signal)
      const paused = gate.remainingMs()
      if (paused > 0) await sleep(paused)
      throwIfAborted(init.signal)

      const headers = new Headers(init.headers)
      if (!opts.anonymous) {
        const token = await cfg.getToken(refreshed ? { forceRefresh: true } : undefined)
        headers.set('Authorization', `Bearer ${token}`)
      }

      let response: Response
      const release = await limiter.acquire(opts.priority)
      try {
        response = await doFetch(fullUrl, { ...init, headers })
      } catch (error) {
        release()
        if (isAbort(error)) throw error
        if (attempt + 1 < policy.maxAttempts && isRetryable({ networkError: true })) {
          await sleep(retryDelayMs(attempt, null, policy, now(), random))
          attempt += 1
          continue
        }
        throw new GraphNetworkError(error)
      }
      release()

      const date = response.headers.get('Date')
      if (date && cfg.onServerDate) {
        try {
          cfg.onServerDate(date)
        } catch {
          /* the clock is a consumer, never a dependency */
        }
      }

      if (response.ok || opts.accept?.includes(response.status)) return response

      // A forced refresh is a different request (a new token), not a repeat of this one, so it does
      // not spend an attempt. It happens at most once.
      if (response.status === 401 && !opts.anonymous) {
        if (!refreshed) {
          refreshed = true
          continue
        }
        throw new InteractiveSignInRequiredError()
      }

      // A refused precondition is an answer, not a fault. Never retried: the same request would lose
      // the same way. A 409/412 on an UNCONDITIONAL request stays a plain GraphError.
      const c = opts.conditional
      if (c && c.pre.kind !== 'none') {
        const s = response.status
        if (s === 412 || s === 409 || (s === 404 && c.pre.kind === 'if-match')) {
          throw new DocConflictError(c.path, s)
        }
      }

      const retryAfter = response.headers.get('Retry-After')
      const delay = retryDelayMs(attempt, retryAfter, policy, now(), random)
      if (isRetryable({ status: response.status }) && attempt + 1 < policy.maxAttempts) {
        if (isThrottle(response.status)) gate.pauseFor(delay)
        else await sleep(delay)
        attempt += 1
        continue
      }

      const body = await response.text().catch(() => '')
      const code = graphErrorCode(body)
      if (isThrottle(response.status)) throw new GraphThrottledError(response.status, code, body, delay)
      throw new GraphError(response.status, code, body)
    }
  }

  // ------------------------------------------------------------------------------- root check
  function check(): Promise<boolean> {
    if (root.kind !== 'approot') return Promise.resolve(false)
    if (!verified) {
      const account = currentAccount()
      const recalled = recall(account)
      const run = (async (): Promise<boolean> => {
        // `id` is only asked for when the app opted into memory, so today's request is unchanged.
        const select = memory ? 'id,name,parentReference' : 'name,parentReference'
        const response = await send(`${GRAPH_BASE}/${rootExpr}?$select=${select}`)
        const item = await jsonOf(response)
        const name = typeof item?.name === 'string' ? item.name : ''
        const parent = item?.parentReference
        const parentPath =
          parent && typeof parent === 'object' && typeof (parent as { path?: unknown }).path === 'string'
            ? (parent as { path: string }).path
            : ''
        const nameOk = name.toLowerCase() === root.expectFolderName.toLowerCase()
        const parentOk = /\/Apps$/i.test(parentPath)
        if (!nameOk || !parentOk) throw new AppRootMismatchError(root.expectFolderName, name, parentPath)
        if (!memory || !account) return false
        const id = typeof item?.id === 'string' ? item.id : null
        const path = `${parentPath}/${name}`
        const confirmed =
          recalled !== null && recalled.id === id && recalled.path.toLowerCase() === path.toLowerCase()
        // Written only if the account did not change while the check was on the wire.
        if (currentAccount() === account) remember({ v: 1, account, id, path })
        return confirmed
      })()
      verified = run
      run.then(
        () => {
          if (verified === run) verifiedOk = true
        },
        () => {
          // Only success is cached: a mismatch or a transient failure is re-checked next time, and
          // the memory that let early reads out is dropped, so the next session waits as before.
          if (verified === run) verified = null
          forget(account)
        },
      )
    }
    return verified
  }

  function verifyRoot(): Promise<void> {
    return check().then(() => undefined)
  }

  /**
   * Run `op` behind the fail-closed root check. A write (and every op when memory is off, or this
   * account has none) waits for the check, exactly as before. A read with a remembered approot for
   * this account goes out at once, alongside the check, and its result is held until the check
   * passes: on a failed check it is discarded and the check's own error is thrown; on a pass that
   * does not confirm the remembered folder (a recreated folder, a new id) it is discarded and the
   * read runs again, now behind the passed check. With `earlyWithoutMemory`, a read on an account
   * with no memory also goes out at once; any pass delivers it (there is no id to confirm).
   */
  async function rooted<T>(kind: 'read' | 'write', op: () => Promise<T>): Promise<T> {
    if (root.kind !== 'approot' || verifiedOk) return op()
    const account = kind === 'read' ? currentAccount() : null
    const recalled = recall(account) !== null
    if (!account || (!recalled && !memory?.earlyWithoutMemory)) {
      await check()
      return op()
    }
    const pending = check()
    const early = op()
    early.catch(() => undefined) // its outcome is only looked at once the check has passed
    const confirmed = await pending
    return confirmed || !recalled ? early : op()
  }

  /** The public raw request: the same loop, behind the same fail-closed root check as every path
   *  method, unless the call is pre-authenticated (`anonymous`) or says it targets outside the root
   *  (`outsideRoot`, e.g. `/me`). Internal callers use `send` because they have already verified. */
  async function request(url: string, init: RequestInit = {}, opts: RequestOptions = {}): Promise<Response> {
    if (opts.anonymous || opts.outsideRoot) return send(url, init, opts)
    return rooted(isReadInit(init) ? 'read' : 'write', () => send(url, init, opts))
  }

  // ------------------------------------------------------------------------------- operations
  function listAll<T>(rel: string, select?: string, opts?: ListOptions & { missing?: 'empty' }): Promise<T[]>
  function listAll<T>(rel: string, select: string | undefined, opts: ListOptions & { missing: 'null' }): Promise<T[] | null>
  function listAll<T>(rel: string, select?: string, opts: ListOptions = {}): Promise<T[] | null> {
    return rooted('read', () => listUnrooted<T>(rel, select, opts))
  }

  async function listUnrooted<T>(rel: string, select: string | undefined, opts: ListOptions): Promise<T[] | null> {
    const priority: RequestOptions = opts.priority ? { priority: opts.priority } : {}
    const items: T[] = []
    let url: string | null = childrenUrl(rel) + (select ? `?$select=${encodeURIComponent(select)}` : '')
    let first = true
    while (url) {
      // 404 on the first page is "the folder is not there yet": `[]` by default, `null` when the
      // caller asked to tell missing from empty. A failure on any later page throws: a partial list
      // would read as "those files were deleted".
      const response: Response = await send(url, {}, first ? { accept: [404], ...priority } : priority)
      if (first && response.status === 404) return opts.missing === 'null' ? null : []
      first = false
      const page = await jsonOf(response)
      if (!page) throw new GraphError(response.status, undefined, '', 'OneDrive sent an unreadable folder listing.')
      const value = page.value
      if (Array.isArray(value)) items.push(...(value as T[]))
      const next = page['@odata.nextLink']
      url = typeof next === 'string' && next ? next : null
    }
    return items
  }

  function readWithTag(rel: string, opts: ReadOptions = {}): Promise<TaggedRead | null> {
    return rooted('read', () => readUnrooted(rel, opts))
  }

  async function readUnrooted(rel: string, opts: ReadOptions): Promise<TaggedRead | null> {
    const priority: RequestOptions = opts.priority ? { priority: opts.priority } : {}
    // Tag FIRST, then bytes (trakr's ordering). If the file moves between the two we hold an older
    // tag with newer content, and the next write fails as a conflict — the safe direction to be
    // wrong in. The other order would let a stale write through.
    const meta = await send(`${itemUrl(rel)}?$select=id,eTag,cTag`, {}, { accept: [404], ...priority })
    if (meta.status === 404) return null
    const item = await jsonOf(meta)
    const tag = contentTagOf(item)
    if (tag === null) throw new GraphError(meta.status, undefined, '', 'OneDrive sent no version tag for a file.')
    const response = await send(contentUrl(rel), {}, { accept: [404], ...priority })
    if (response.status === 404) return null
    return { tag, eTag: item && typeof item.eTag === 'string' ? item.eTag : null, response }
  }

  function preconditionHeaders(pre: WritePrecondition, headers: Headers): string {
    if (pre.kind === 'if-match') headers.set('If-Match', pre.tag)
    return pre.kind === 'expect-absent' ? '?@microsoft.graph.conflictBehavior=fail' : ''
  }

  async function write(rel: string, body: BodyInit, contentType: string, pre: WritePrecondition): Promise<WrittenItem> {
    const size = byteLength(body)
    if (size !== null && size > SIMPLE_UPLOAD_MAX) {
      // Past Graph's simple-upload ceiling: the same write, through an upload session.
      if (typeof body === 'string') return uploadLarge(rel, new TextEncoder().encode(body), pre)
      if (body instanceof Blob || body instanceof ArrayBuffer || body instanceof Uint8Array) {
        return uploadLarge(rel, body, pre)
      }
      throw new RangeError(`A ${size}-byte body is over the simple-upload limit; pass a Blob, ArrayBuffer or Uint8Array.`)
    }
    await verifyRoot()
    const headers = new Headers({ 'Content-Type': contentType })
    const query = preconditionHeaders(pre, headers)
    const response = await send(
      contentUrl(rel) + query,
      { method: 'PUT', headers, body },
      { conditional: { path: rel, pre } },
    )
    return writtenItemOf(await jsonOf(response), wasRecreated(pre, response.status))
  }

  async function uploadLarge(
    rel: string,
    bytes: Blob | ArrayBuffer | Uint8Array,
    pre: WritePrecondition,
    opts: UploadOptions = {},
  ): Promise<WrittenItem> {
    const { onProgress, signal } = opts
    const priority: RequestOptions = opts.priority ? { priority: opts.priority } : {}
    throwIfAborted(signal)
    await verifyRoot()
    const blob = toBlob(bytes)
    const size = blob.size
    const conditional = { path: rel, pre }
    const progress = (sent: number): void => {
      if (!onProgress) return
      try {
        onProgress(Math.min(sent, size), size)
      } catch {
        /* a progress bar is a consumer, never a dependency */
      }
    }

    async function createSession(): Promise<string> {
      const headers = new Headers({ 'Content-Type': 'application/json' })
      if (pre.kind === 'if-match') headers.set('If-Match', pre.tag)
      const behaviour = pre.kind === 'expect-absent' ? 'fail' : 'replace'
      const response = await send(
        `${itemUrl(rel)}:/createUploadSession`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ item: { '@microsoft.graph.conflictBehavior': behaviour } }),
          ...(signal ? { signal } : {}),
        },
        { conditional, ...priority },
      )
      const session = await jsonOf(response)
      const uploadUrl = session?.uploadUrl
      if (typeof uploadUrl !== 'string' || !uploadUrl) {
        throw new GraphError(response.status, undefined, '', 'OneDrive did not open an upload session.')
      }
      return uploadUrl
    }

    let uploadUrl = await createSession()
    let freshSessionUsed = false
    let resumes = 0
    let offset = 0

    for (;;) {
      // Between chunks is where an abort is cheapest: nothing half-sent.
      throwIfAborted(signal)
      const end = Math.min(offset + UPLOAD_CHUNK, size)
      let response: Response
      try {
        // Every chunk goes through the retry policy. Pre-authenticated URL: no Authorization header.
        response = await send(
          uploadUrl,
          {
            method: 'PUT',
            headers: { 'Content-Range': `bytes ${offset}-${end - 1}/${size}` },
            body: blob.slice(offset, end),
            ...(signal ? { signal } : {}),
          },
          // Not `conditional`: a 404 on a chunk means the SESSION is gone, not the file.
          { anonymous: true, ...priority },
        )
      } catch (error) {
        // The commit (final chunk) is where `conflictBehavior=fail` or a moved tag is enforced.
        if (error instanceof GraphError && (error.status === 409 || error.status === 412) && pre.kind !== 'none') {
          throw new DocConflictError(rel, error.status)
        }
        if (isAbort(error) || signal?.aborted || resumes >= MAX_UPLOAD_RESUMES) throw error
        resumes += 1
        // Ask the session where it actually got to, and resume there.
        const status = await send(uploadUrl, signal ? { signal } : {}, { anonymous: true, accept: [404], ...priority })
        if (status.status === 404) {
          // The session expired or was lost: start a fresh one, once.
          if (freshSessionUsed) throw error
          freshSessionUsed = true
          uploadUrl = await createSession()
          offset = 0
          continue
        }
        const resumeAt = nextRangeStart(await jsonOf(status))
        if (resumeAt === null) throw error
        offset = resumeAt
        continue
      }

      if (response.status === 202) {
        offset = nextRangeStart(await jsonOf(response)) ?? end
        progress(offset)
        continue
      }
      // 200 or 201: the final chunk landed and Graph answered with the DriveItem. Check it holds
      // every byte: a session resumed from a wrong offset can commit a file of the wrong length, and
      // that must fail loudly rather than be recorded as written. An item with no `size` is not
      // checked (Graph always sends one; a test double may not).
      const item = await jsonOf(response)
      const stored = item?.size
      if (typeof stored === 'number' && stored !== size) {
        throw new GraphError(
          response.status,
          'sizeMismatch',
          '',
          `OneDrive stored ${stored} bytes of a ${size}-byte upload.`,
        )
      }
      progress(size)
      return writtenItemOf(item, wasRecreated(pre, response.status))
    }
  }

  async function remove(rel: string, pre?: { kind: 'if-match'; tag: string }): Promise<void> {
    await verifyRoot()
    const headers = new Headers()
    if (pre) headers.set('If-Match', pre.tag)
    // 404 = success: the file is gone either way.
    await send(itemUrl(rel), { method: 'DELETE', headers }, { accept: [404], ...(pre ? { conditional: { path: rel, pre } } : {}) })
  }

  async function lookupFolderAt(url: string): Promise<string | null> {
    const response = await send(`${url}?$select=id,folder`, {}, { accept: [404] })
    if (response.status === 404) return null
    const item = await jsonOf(response)
    return typeof item?.id === 'string' ? item.id : null
  }

  const lookupFolderId = (rel: string): Promise<string | null> => lookupFolderAt(itemUrl(rel))

  /** POST a folder named `name` into `childrenUrl`; a 409 means another tab or device won the race,
   *  so `relookup` finds theirs. */
  async function createFolder(childrenUrlOfParent: string, name: string, relookup: () => Promise<string | null>): Promise<string> {
    const response = await send(
      childrenUrlOfParent,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, folder: {}, '@microsoft.graph.conflictBehavior': 'fail' }),
      },
      { accept: [409] },
    )
    if (response.status === 409) {
      const raced = await relookup()
      if (raced) return raced
      throw new GraphError(409, undefined, '', 'OneDrive reported a folder that then could not be found.')
    }
    const created = await jsonOf(response)
    if (typeof created?.id !== 'string') throw new GraphError(response.status, undefined, '', 'OneDrive created a folder without an id.')
    return created.id
  }

  /** The transitional `{ kind: 'path' }` root, created segment by segment from the drive root
   *  (`Apps`, then `Apps/Contently`) on an account that has never run the app. Only called once the
   *  full root path is known to be missing. */
  async function createRootPath(segments: readonly string[]): Promise<string> {
    const lookup = (n: number): Promise<string | null> =>
      lookupFolderAt(`${GRAPH_BASE}/me/drive/root:/${segments.slice(0, n).map((s) => encodeURIComponent(s)).join('/')}`)
    // Find the deepest ancestor that exists, walking up (the common case is that `Apps` exists).
    let have = segments.length - 1
    let parentId: string | null = null
    while (have > 0) {
      parentId = await lookup(have)
      if (parentId) break
      have -= 1
    }
    for (let n = have + 1; n <= segments.length; n += 1) {
      const name = segments[n - 1] ?? ''
      const into: string = parentId
        ? `${GRAPH_BASE}/me/drive/items/${encodeURIComponent(parentId)}/children`
        : `${GRAPH_BASE}/me/drive/root/children`
      parentId = await createFolder(into, name, () => lookup(n))
    }
    if (!parentId) throw new GraphError(404, undefined, '', 'OneDrive has no folder for this app.')
    return parentId
  }

  function ensureFolder(rel: string): Promise<string> {
    const key = encodeRel(rel)
    const cached = folderIds.get(key)
    if (cached) return cached
    const pending = (async (): Promise<string> => {
      await verifyRoot()
      const existing = await lookupFolderId(rel)
      if (existing) return existing
      if (key === '') {
        const segments = root.kind === 'path' ? root.path.split('/').filter((s) => s !== '') : []
        // Approot: Graph makes the folder itself on first use, so a 404 here is a real fault.
        if (segments.length === 0) throw new GraphError(404, undefined, '', 'OneDrive has no folder for this app.')
        return createRootPath(segments)
      }
      const parentId = await ensureFolder(parentRel(rel))
      return createFolder(
        `${GRAPH_BASE}/me/drive/items/${encodeURIComponent(parentId)}/children`,
        leafName(rel),
        () => lookupFolderId(rel),
      )
    })()
    folderIds.set(key, pending)
    pending.catch(() => folderIds.delete(key))
    return pending
  }

  function forgetFolder(rel: string): void {
    const key = encodeRel(rel)
    if (key === '') {
      folderIds.clear()
      return
    }
    for (const k of [...folderIds.keys()]) if (k === key || k.startsWith(`${key}/`)) folderIds.delete(k)
  }

  function delta<T>(deltaLink?: string): Promise<DeltaResult<T>> {
    return rooted('read', () => deltaUnrooted<T>(deltaLink))
  }

  async function deltaUnrooted<T>(deltaLink?: string): Promise<DeltaResult<T>> {
    const start = subUrl('', 'delta')
    let reset = false
    let url = deltaLink ?? start
    const items: T[] = []
    for (;;) {
      const response = await send(url, {}, { accept: [410] })
      if (response.status === 410) {
        // The token expired or the server reset it: restart from scratch, once.
        if (reset) throw new GraphError(410, undefined, '', 'OneDrive could not restart its change feed.')
        reset = true
        items.length = 0
        url = start
        continue
      }
      const page = await jsonOf(response)
      if (Array.isArray(page?.value)) items.push(...(page.value as T[]))
      const next = page?.['@odata.nextLink']
      if (typeof next === 'string' && next) {
        url = next
        continue
      }
      const link = page?.['@odata.deltaLink']
      if (typeof link !== 'string' || !link) {
        throw new GraphError(response.status, undefined, '', 'OneDrive ended its change feed without a delta link.')
      }
      return { items, deltaLink: link, reset }
    }
  }

  return {
    request,
    itemUrl,
    contentUrl,
    childrenUrl,
    verifyRoot,
    listAll,
    readWithTag,
    write,
    uploadLarge,
    remove,
    ensureFolder,
    forgetFolder,
    delta,
    reset(): void {
      verified = null
      verifiedOk = false
      folderIds.clear()
    },
    forgetRememberedRoot(): void {
      forget(currentAccount())
    },
  }
}
